import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Search, User, Mail, Calendar, Package,
  ExternalLink, Send, ShieldCheck, ShieldBan, Clock, MessageSquare, Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UserDetails {
  auth: {
    id: string;
    email: string;
    created_at: string;
    last_sign_in_at: string | null;
    email_confirmed_at: string | null;
    provider: string;
    full_name: string | null;
  };
  profile: { full_name: string | null; avatar_url: string | null } | null;
  orders: any[];
  reviews: any[];
  recent_messages: any[];
  abandoned_surveys: any[];
  is_blocked: boolean;
}

const statusLabels: Record<string, string> = {
  submitted: "Submitted", in_review: "In Review", drafting_lyrics: "Writing Lyrics",
  composing: "Composing", ready_for_review: "Ready for Review",
  revision_requested: "Changes Requested", approved: "Approved", completed: "Completed",
};

const AdminUserTest = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();

  const [searchEmail, setSearchEmail] = useState("");
  const [searching, setSearching] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);

  // All customers for search
  const [allCustomers, setAllCustomers] = useState<{ id: string; email: string }[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<{ id: string; email: string }[]>([]);

  // Login link
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginLink, setLoginLink] = useState<string | null>(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  // Test email
  const [emailType, setEmailType] = useState("status_change");
  const [emailStatus, setEmailStatus] = useState("submitted");
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (!authLoading && !adminLoading) {
      if (!user) navigate("/login");
      else if (!isAdmin) navigate("/dashboard");
    }
  }, [user, isAdmin, authLoading, adminLoading, navigate]);

  useEffect(() => {
    const fetchCustomers = async () => {
      const { data } = await supabase.functions.invoke("get-admin-emails", {
        body: { type: "all_customers" },
      });
      if (data?.emails) setAllCustomers(data.emails);
    };
    if (isAdmin) fetchCustomers();
  }, [isAdmin]);

  useEffect(() => {
    if (!searchEmail.trim()) {
      setFilteredCustomers([]);
      return;
    }
    const q = searchEmail.toLowerCase();
    setFilteredCustomers(allCustomers.filter((c) => c.email.toLowerCase().includes(q)).slice(0, 10));
  }, [searchEmail, allCustomers]);

  const loadUserDetails = async (userId: string) => {
    setSearching(true);
    setUserDetails(null);
    setLoginLink(null);
    try {
      const { data, error } = await supabase.functions.invoke("manage-customer", {
        body: { action: "get_user_details", user_id: userId },
      });
      if (error || data?.error) {
        toast.error(data?.error || "Failed to load user details");
      } else {
        setUserDetails(data);
        if (data.orders?.length > 0) setSelectedOrderId(data.orders[0].id);
      }
    } catch {
      toast.error("Failed to load user details");
    }
    setSearching(false);
  };

  const handleGenerateLoginLink = async () => {
    if (!userDetails) return;
    setLoginLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-customer", {
        body: { action: "generate_login_link", user_id: userDetails.auth.id },
      });
      if (error || data?.error) {
        toast.error(data?.error || "Failed to generate login link");
      } else if (data.action_link) {
        setLoginLink(data.action_link);
        setLoginDialogOpen(true);
      }
    } catch {
      toast.error("Failed to generate login link");
    }
    setLoginLoading(false);
  };

  const handleSendTestEmail = async () => {
    if (!userDetails || !selectedOrderId) return;
    setSendingEmail(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-customer", {
        body: {
          action: "send_test_email",
          user_id: userDetails.auth.id,
          email_type: emailType,
          order_id: selectedOrderId,
          new_status: emailType === "status_change" ? emailStatus : undefined,
        },
      });
      if (error || data?.error) {
        toast.error(data?.error || "Failed to send test email");
      } else {
        toast.success(data.message || "Test email sent!");
      }
    } catch {
      toast.error("Failed to send test email");
    }
    setSendingEmail(false);
  };

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate("/admin")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>

          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">User Testing</h1>
          <p className="text-muted-foreground mb-8">Look up a customer account, view details, login as them, or send test emails.</p>

          {/* Search */}
          <div className="relative mb-8">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="pl-10"
            />
            {filteredCustomers.length > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 bg-card border border-border rounded-lg shadow-lg mt-1 max-h-60 overflow-auto">
                {filteredCustomers.map((c) => (
                  <button
                    key={c.id}
                    className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors text-sm flex items-center gap-2"
                    onClick={() => {
                      setSearchEmail(c.email);
                      setFilteredCustomers([]);
                      loadUserDetails(c.id);
                    }}
                  >
                    <User className="w-4 h-4 text-muted-foreground" />
                    {c.email}
                  </button>
                ))}
              </div>
            )}
          </div>

          {searching && (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {userDetails && (
            <div className="space-y-6">
              {/* User Info Card */}
              <div className="bg-card rounded-2xl shadow-soft p-6">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-foreground">
                        {userDetails.auth.full_name || userDetails.profile?.full_name || "No Name"}
                      </h2>
                      <p className="text-muted-foreground">{userDetails.auth.email}</p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {userDetails.is_blocked ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-destructive/15 text-destructive border border-destructive/30">
                            <ShieldBan className="w-3 h-3" /> Blocked
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/30">
                            <ShieldCheck className="w-3 h-3" /> Active
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">Provider: {userDetails.auth.provider}</span>
                      </div>
                    </div>
                  </div>
                  <Button onClick={handleGenerateLoginLink} disabled={loginLoading} variant="outline">
                    {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                    Login as User
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  {[
                    { icon: Calendar, label: "Joined", value: new Date(userDetails.auth.created_at).toLocaleDateString() },
                    { icon: Clock, label: "Last Sign In", value: userDetails.auth.last_sign_in_at ? new Date(userDetails.auth.last_sign_in_at).toLocaleDateString() : "Never" },
                    { icon: Package, label: "Orders", value: String(userDetails.orders.length) },
                    { icon: Star, label: "Reviews", value: String(userDetails.reviews.length) },
                  ].map((item) => (
                    <div key={item.label} className="bg-muted/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                        <item.icon className="w-3.5 h-3.5" /> {item.label}
                      </div>
                      <p className="font-semibold text-foreground">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tabs for details */}
              <Tabs defaultValue="orders" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="orders">Orders ({userDetails.orders.length})</TabsTrigger>
                  <TabsTrigger value="messages">Messages ({userDetails.recent_messages.length})</TabsTrigger>
                  <TabsTrigger value="test-email">Send Test Email</TabsTrigger>
                </TabsList>

                <TabsContent value="orders">
                  {userDetails.orders.length === 0 ? (
                    <div className="bg-card rounded-xl p-8 text-center text-muted-foreground">No orders found.</div>
                  ) : (
                    <div className="bg-card rounded-xl shadow-soft divide-y divide-border overflow-hidden">
                      {userDetails.orders.map((order) => (
                        <div key={order.id} className="p-4 flex items-center justify-between hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => navigate(`/admin/order/${order.id}`)}>
                          <div>
                            <p className="font-semibold text-foreground">
                              <span className="text-muted-foreground font-mono text-sm mr-2">#{order.order_number}</span>
                              {order.recipient_names}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {order.event_type} · {statusLabels[order.status] || order.status} · {order.plan_type}
                              {order.price_paid != null && ` · $${Number(order.price_paid).toFixed(2)}`}
                            </p>
                          </div>
                          <span className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="messages">
                  {userDetails.recent_messages.length === 0 ? (
                    <div className="bg-card rounded-xl p-8 text-center text-muted-foreground">No recent messages.</div>
                  ) : (
                    <div className="bg-card rounded-xl shadow-soft divide-y divide-border overflow-hidden">
                      {userDetails.recent_messages.map((msg) => (
                        <div key={msg.id} className="p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              {msg.is_from_team ? "Team" : "Customer"} · {new Date(msg.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-foreground">{msg.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="test-email">
                  <div className="bg-card rounded-xl shadow-soft p-6 space-y-4">
                    <h3 className="font-semibold text-foreground">Send Test Email</h3>
                    <p className="text-sm text-muted-foreground">This will send a real email to {userDetails.auth.email}.</p>

                    {userDetails.orders.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No orders — cannot send test emails.</p>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-foreground mb-1 block">Email Type</label>
                            <Select value={emailType} onValueChange={setEmailType}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="status_change">Status Change</SelectItem>
                                <SelectItem value="new_order">New Order Confirmation</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <label className="text-sm font-medium text-foreground mb-1 block">Order</label>
                            <Select value={selectedOrderId} onValueChange={setSelectedOrderId}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {userDetails.orders.map((o) => (
                                  <SelectItem key={o.id} value={o.id}>#{o.order_number} — {o.recipient_names}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {emailType === "status_change" && (
                          <div>
                            <label className="text-sm font-medium text-foreground mb-1 block">Status to Simulate</label>
                            <Select value={emailStatus} onValueChange={setEmailStatus}>
                              <SelectTrigger className="max-w-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Object.entries(statusLabels).map(([val, label]) => (
                                  <SelectItem key={val} value={val}>{label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        <Button onClick={handleSendTestEmail} disabled={sendingEmail || !selectedOrderId}>
                          {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                          Send Test Email
                        </Button>
                      </>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </main>
      <Footer />

      {/* Login Link Dialog */}
      <Dialog open={loginDialogOpen} onOpenChange={setLoginDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Login Link Generated</DialogTitle>
            <DialogDescription>
              Use this link to sign in as {userDetails?.auth.email}. This link is single-use and expires shortly.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs font-mono break-all text-foreground">{loginLink}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoginDialogOpen(false)}>Close</Button>
            <Button onClick={() => {
              if (loginLink) {
                navigator.clipboard.writeText(loginLink);
                toast.success("Link copied to clipboard");
              }
            }}>
              Copy Link
            </Button>
            <Button variant="hero" onClick={() => {
              if (loginLink) window.open(loginLink, "_blank");
            }}>
              <ExternalLink className="w-4 h-4 mr-2" /> Open in New Tab
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUserTest;
